import React, { memo, useState, useEffect, useCallback } from "react";
import ResponsiveDrawer from "../layout/ResponsiveDrawer"
import { useDnDSort } from "../templates/useDnDSort";
import { useRecoilState } from "recoil";
import { memberState } from "../../store/memberState";
import styled from "@emotion/styled"
import { RandomArangementButton } from "../atoms/button/RandomArangementButton";
import { BackButton } from "../atoms/button/BackButton";
import NameIcon from "../templates/NameIcon";
import {CopyUrlButton} from "../atoms/button/CopyUrlButton";
import { websocketState } from '../../store/websocketState';
import { LockDisplayButton } from "../atoms/button/LockDisplayButton";
import ClipLoader from "react-spinners/ClipLoader";
import { loadingState } from "../../store/loadingState";
import { useRef } from "react";
import { partyNameState } from '../../store/partyNameState';
import { tableNumberState } from '../../store/tableNumberState';
import { trackEvent } from '../../utils/gtag';

export const areArraysEqual = (arr1: any[], arr2: any[]): boolean => {
  // 配列の長さが異なる場合、不一致とする
  if (arr1.length !== arr2.length) return false;

  // 配列の順序を含めて各要素を比較
  return arr1.every((item1, index) => {
    const item2 = arr2[index];
    // オブジェクトのプロパティをすべて比較する
    return Object.keys(item1).every(
      (key) => item1[key] === item2[key]
    );
  });
};

type Style<T extends HTMLElement> = React.HTMLAttributes<T>["style"];
/**
 * @description ドラッグ＆ドロップ並び替えサンプルのコンポーネント
 */
export const Result = () => {
  const [memberInfo, setMemberInfo] = useRecoilState(memberState);
  const [partyName, setPartyName] = useRecoilState(partyNameState);

  // URLからパーティードキュメントIDを取得
  const pathname = window.location.pathname;
  // /party/以降のパスを取得
  const party_id = pathname.split("/party/")[1];
  const [ws, setWs] = useRecoilState(websocketState);
  // Loadingフラグを管理するフラグ
  const [isLoading, setIsLoading] = useRecoilState(loadingState);
  // Doorのタッチ前初期座標
  const [entryPosition, setEntryPosition] = useState({ x: 0, y: 0 });
  // Doorのタッチ時の初期座標（タッチ操作の座標計算の運動量計算で使用）
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  // ドアの座標が変更されるのを監視する
  const entranceRef = useRef<HTMLDivElement>(null);
  // table数
  const [tableNumber, setTableNumber] = useRecoilState(tableNumberState);

  // 元となるcontainer, memnerInfo, tableのスタイル
  const containerStyle: Style<HTMLDivElement> = {
    display: "grid",
    gridTemplateColumns: "repeat(9, 1fr)", // 9列グリッド
    gap: "10px",
    alignItems: "center",
    justifyContent: "center",
    position: "relative", // table, table2配置のため
  };
  const imageCardStyle: Style<HTMLDivElement> = {
    cursor: "grab",
    userSelect: "none",
    width: "30px",
    height: "30px",
    overflow: "hidden",
    borderRadius: "100px",
    display: "flex",
    opacity: 1, // デフォルトの不透明度
  };
  const tableStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    backgroundColor: "#5c3a1e", // 深い茶色
    borderRadius: "12px",
    boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
    border: "2px solid #3e2a14",
    backgroundImage: `linear-gradient(145deg, #6e4b2a, #4b2e1a)`,
  };
  // 机が1つかつ、memberInfoが12人以上の時のスタイル
  const imageCardSizeStyle = memberInfo.length <= 12
    ? { width: "60px", height: "60px" }
    : { width: "30px", height: "30px" };
  const tableGridRow = memberInfo.length <= 12
    ? { gridRow: `1 / ${Math.ceil(memberInfo.length / 2) + 1}` }
    : { gridRow: `1 / ${Math.ceil(memberInfo.length / 2) + 1}` };
  // 机が1つかつ、memberInfoが12人以下の時のスタイル
  const oneTableLeftImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "2 / span 2", // 2列目〜3列目
    justifySelf: "center",
    ...imageCardSizeStyle,
  };
  const oneTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "4 / span 3", // 4〜6列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 2) + 1}`,
  };
  const oneTableRightImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "7 / span 2", // 7列目〜8列目
    justifySelf: "center",
    ...imageCardSizeStyle,
  };
  // 机が2つの時のスタイル
  const leftTwoTableLeftImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "1", // 1列目
    justifySelf: "center",
  };
  const leftTwoTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "2 / span 2", // 2〜3列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 4) + 1}`,
  };
  const leftTwoTableRightImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "4", // 4列目
    justifySelf: "center",
  };
  const rightTwoTableLeftImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "5", // 1列目
    justifySelf: "center",
  };
  const rightTwoTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "6 / span 2", // 2〜3列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 4) + 1}`,
  };
  const rightTwoTableRightImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "8", // 4列目
    justifySelf: "center",
  };
  // 机が3つの時のスタイル
  const leftThreeTableLeftImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "1", // 1列目
    justifySelf: "center",
  };
  const leftThreeTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "2", // 2列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 6) + 1}`,
  };
  const lefthreeTableRightImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "3", // 3列目
    justifySelf: "center",
  };
  const centerThreeTableLeftImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "4", // 4列目
    justifySelf: "center",
  };
  const centerThreeTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "5", // 5列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 6) + 1}`,
  };
  const centerThreeTableRightImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "6", // 6列目
    justifySelf: "center",
  };
  const rightThreeTableLeftImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "7", // 7列目
    justifySelf: "center",
  };
  const rightThreeTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "8", // 8列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 6) + 1}`,
  };
  const rightThreeTableRightImageCardStyle: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "9", // 4列目
    justifySelf: "center",
  };
  // 机が4つの時のスタイル
  const upperLeftFourTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "2 / span 2", // 2〜3列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 8) + 1}`,
  };
  const upperRightFourTableStyle: React.CSSProperties = {
    ...table2Style,
    gridColumn: "6 / span 2", // 6〜7列目
    gridRow: `1 / ${Math.ceil(memberInfo.length / 8) + 1}`,
  };
  const lowerLeftFourTableStyle: React.CSSProperties = {
    ...tableStyle,
    gridColumn: "2 / span 2", // 2列目〜3列目
    gridRow: `${Math.ceil(memberInfo.length / 8) + 1} / ${Math.ceil(memberInfo.length / 4 + 1)}`,
  };
  const lowerRightFourTableStyle: React.CSSProperties = {
    ...table2Style,
    gridColumn: "6 / span 2", // 6〜7列目
    gridRow: `${Math.ceil(memberInfo.length / 8) + 1} / ${Math.ceil(memberInfo.length / 4 + 1)}`,
  };

  /////////////////////
  const imageCardStyleRightMid: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "5", // 5列目
    justifySelf: "center",
  };
  
  const imageCardStyleRightEnd: Style<HTMLDivElement> = {
    ...imageCardStyle,
    gridColumn: "8", // 8列目
    justifySelf: "center",
  };
  // table数に応じたtableのスタイル変更

  const table2DynamicStyle: React.CSSProperties = {
    ...table2Style,
    gridRow: `1 / ${Math.ceil(memberInfo.length / 8) + 1}`,
  };
  
  const table3DynamicStyle: React.CSSProperties = {
    ...tableStyle,
    gridRow: `${Math.ceil(memberInfo.length / 8) + 1} / ${Math.ceil(memberInfo.length / 4 + 1)}`,
  };
  const table4DynamicStyle: React.CSSProperties = {
    ...table2Style,
    gridRow: `${Math.ceil(memberInfo.length / 8) + 1} / ${Math.ceil(memberInfo.length / 4 + 1)}`,
  };
  
  
  
  useEffect(() => {
    // 共有ユーザが送付されたURLからDynamoDBにデータ取得（WebSocketがまだ接続されていない場合にのみ接続を開始）
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // ローディングフラグ開始
      setIsLoading(true);
      const wsInstance = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL as string);
      // 接続が確立されたとき、reload時は③
      wsInstance.onopen = () => {
        // WebSocketをRecoilの状態管理に保存
        setWs(wsInstance);
        // WebSocket経由でDynamoDBデータ取得リクエストを送信、reload時は④
        wsInstance.send(JSON.stringify({
          action: 'getMemberInfo',  // DynamoDBからデータ取得するためのアクション
          party_id: party_id  // クエリするparty_idを指定
        }));
        wsInstance.send(JSON.stringify({
          action: 'getDoorPosition',  // DynamoDBからデータ取得するためのアクション
          party_id: party_id  // クエリするparty_idを指定
        }));
      };
      wsInstance.onmessage = (event) => {
        if(event.data === ""){
          return
        }
        const data = JSON.parse(event.data);
        // 受信：DynamoDB更新するgetMemberInfo APIもしくはupdateMemberInfo API叩かれた際に全connectionIDが最新情報を受信
        if (data.type === "get_member_info" || data.type === "update_member_info") {
          // DynamoDBから受信したmemberInfo vs reactでrecoilで管理しているmemberInfoが異なれば、DynamoDBから受信したデータでrecoilを更新する
          if (areArraysEqual(data.member_info, memberInfo)) {
            setIsLoading(false);
          } else {
            // 必要に応じて状態やUIを更新
            try {
              // memberInfo = data.member_info
              setMemberInfo(data.member_info);
              setPartyName(data.party_name);
              setTableNumber(data.table_number)
              // ローディングフラグ終了
              setIsLoading(false);
            } catch (error) {
              console.error('Error updating memberInfo:', error);
              // ローディングフラグ解除（エラー時）
              setIsLoading(false); 
            }
          }
        // 受信：DynamoDB更新するupdateDoorPosition API叩かれた際に全connectionIDが最新情報を受信
        }else if(data.type === "get_door_position" || data.type === "update_door_position"){
          try {
            setEntryPosition(data.door_position);
          } catch (error) {
            console.error('Error updating entryPosition:', error);
          }
        }else{
          return;
        }
      };
      wsInstance.onerror = (error) => {
          console.error('WebSocket error:', error);
          // ローディングフラグ解除（エラー時）
          setIsLoading(false); 
      };
      wsInstance.onclose = () => {
          console.log('WebSocket connection closed');
          // ローディングフラグ解除（エラー時）
          setIsLoading(false); 
      };
    }
  }, [ws, setWs, setMemberInfo]);

  // ドアの座標（entryPosition）が変更されるたびにアイコンが動くようにする
  useEffect(() => {
    if (entranceRef.current) {
      entranceRef.current.style.transform = `translate(${entryPosition.x}px, ${entryPosition.y}px)`;
    }
  }, [entryPosition]); 

  //名前リストを種類ごとにソート
  const results = useDnDSort();

  const disableScroll = useCallback((event: TouchEvent) => {
    event.preventDefault();
  }, []);

  const handleTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    if (!sessionStorage.getItem("partyId")) {
      // ドラッグ無効化時は何もしない
      return; 
    }
    // タッチが始まったときの処理
    const touch = event.touches[0];
    const { clientX, clientY } = touch;
    setStartPosition({ x: clientX, y: clientY });
    // Touch中に画面が動かないようにデフォルトのタッチイベントの動作をキャンセル
    window.addEventListener("touchmove", disableScroll, { passive: false });
    // スクロールを停止
    document.body.style.overflow = 'hidden';
  };
  
  const handleTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    if (!sessionStorage.getItem("partyId")) {
      // ドラッグ無効化時は何もしない
      return; 
    }
    // タッチ中に移動したときの処理
    const touch = event.touches[0];
    const { clientX, clientY } = touch;
    // マウスポインターの移動量を計算
    const x = clientX - startPosition.x + entryPosition.x;
    const y = clientY - startPosition.y + entryPosition.y;
    event.currentTarget.style.transform = `translate(${x}px,${y}px)`;
  };
  
  const handleTouchEnd = (event: React.TouchEvent<HTMLElement>) => {
    if (!sessionStorage.getItem("partyId")) {
      // ドラッグ無効化時は何もしない
      return; 
    }
    // タッチが終了したときの処理
    // タッチ終了時に現在の位置を初期位置として設定
    setEntryPosition({
      x: event.currentTarget.style.transform ? parseInt(event.currentTarget.style.transform.split('(')[1].split('px')[0]) : 0,
      y: event.currentTarget.style.transform ? parseInt(event.currentTarget.style.transform.split(',')[1].split('px')[0]) : 0,
    });
    // GA4: ドア位置タッチ移動イベント送信（差別化機能の実利用計測）
    trackEvent('door_touch_move');
    // DynamoDBにドアの座標をアップデート
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      const wsInstance = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL as string);
      // 接続が確立されたとき、reload時は③
      wsInstance.onopen = () => {
        // WebSocketをRecoilの状態管理に保存
        setWs(wsInstance);
        // WebSocketが開いている場合のみ送信
        if (wsInstance.readyState === WebSocket.OPEN) {
          wsInstance.send(JSON.stringify({
            action: 'updateDoorPosition',
            type: 'doorPosition',
            party_id: party_id,
            data: {
              x: event.currentTarget.style.transform ? parseInt(event.currentTarget.style.transform.split('(')[1].split('px')[0]) : 0,
              y: event.currentTarget.style.transform ? parseInt(event.currentTarget.style.transform.split(',')[1].split('px')[0]) : 0,
            },
          }));
        }
      };
      // エラーハンドリング
      wsInstance.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } else {
      // 既存のWebSocketが開いている場合のみ送信
      ws.send(JSON.stringify({
        action: 'updateDoorPosition',
        type: 'doorPosition',
        party_id: party_id,
        data: {
          x: event.currentTarget.style.transform ? parseInt(event.currentTarget.style.transform.split('(')[1].split('px')[0]) : 0,
          y: event.currentTarget.style.transform ? parseInt(event.currentTarget.style.transform.split(',')[1].split('px')[0]) : 0,
        },
      }));
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    }
    // Touch中に画面が動かないようにデフォルトのタッチイベントの動作をキャンセル
    window.removeEventListener("touchmove", disableScroll);
    // スクロールを有効に戻す
    document.body.style.overflow = 'auto';
  };
  
  return (
    <ResponsiveDrawer>
      <SbodyStyle>
        <div style={buttonContainerStyle}>
          <BackButton/>
          {/* メインユーザの画面のみ（partyId が sessionstorageに格納されてる場合のみ）、「ランダム」を表示 */}
          {sessionStorage.getItem("partyId") && 
            <RandomArangementButton />
          }
          {/* 共有ユーザの画面のみ（partyId が sessionstorageに格納されていない場合のみ）、「共有ページ」を表示 */}
          {!sessionStorage.getItem("partyId") && 
            <LockDisplayButton/>
          }
          <CopyUrlButton />
        </div>
        {isLoading ? (
          <StyledLoader>
            <ClipLoader color="#1976d2" size={100} />
          </StyledLoader>
          ) : (
        <div style={containerStyle}>
          {results.map((item, index) => {
            let positionStyle;

            if (tableNumber === 1) {
              positionStyle = index % 2 === 0
                ? oneTableLeftImageCardStyle
                : oneTableRightImageCardStyle;
            } else if (tableNumber === 2) {
              positionStyle = 
                index % 4 === 0 ? leftTwoTableLeftImageCardStyle :
                index % 4 === 1 ? leftTwoTableRightImageCardStyle :
                index % 4 === 2 ? rightTwoTableLeftImageCardStyle :
                rightTwoTableRightImageCardStyle;
            } else if (tableNumber === 3) {
              positionStyle = 
                index % 6 === 0 ? leftThreeTableLeftImageCardStyle :
                index % 6 === 1 ? lefthreeTableRightImageCardStyle :
                index % 6 === 2 ? centerThreeTableLeftImageCardStyle :
                index % 6 === 3 ? centerThreeTableRightImageCardStyle :
                index % 6 === 4 ? rightThreeTableLeftImageCardStyle :
                rightThreeTableRightImageCardStyle;
            } else if (tableNumber === 4) {
              positionStyle = 
              index % 4 === 0 ? leftTwoTableLeftImageCardStyle :
              index % 4 === 1 ? leftTwoTableRightImageCardStyle :
              index % 4 === 2 ? rightTwoTableLeftImageCardStyle :
              rightTwoTableRightImageCardStyle;
            }
            return (
              <div
                key={item.key}
                style={{ ...positionStyle, opacity: item.isTouched ? 0.5 : 1 }}
                {...item.events}
              >
                <NameIcon userInfo={item.value} />
              </div>
            );
          })}
          {tableNumber === 1 ? (
            <div style={oneTableStyle}></div>
          ) : tableNumber === 2 ? (
            <>
              <div style={leftTwoTableStyle}></div>
              <div style={rightTwoTableStyle}></div>
            </>
          ) : tableNumber === 3 ? (
            <>
              <div style={leftThreeTableStyle}></div>
              <div style={centerThreeTableStyle}></div>
              <div style={rightThreeTableStyle}></div>
            </>
          ) : tableNumber === 4 ? (
            <>
              <div style={upperLeftFourTableStyle}></div>
              <div style={upperRightFourTableStyle}></div>
              <div style={lowerLeftFourTableStyle}></div>
              <div style={lowerRightFourTableStyle}></div>
            </>
          ) : null}
        </div>
        )}
        <div 
          ref={entranceRef}
          style={{ ...entranceStyle }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        ></div>
      </SbodyStyle>
    </ResponsiveDrawer>
  );
};

const SbodyStyle = styled.div`
    height: 100vh,
    display: flex,
    alignItems: center,
    justifyContent: center,
`


const table: React.CSSProperties = {
  gridColumn: "2 / span 2", // 2列目〜3列目
  height: "100%",
  backgroundColor: "rgba(49, 23, 1, 0.623)",
  background: `repeating-radial-gradient(circle at -1000% 0%, rgba(116, 77, 48, 0.7), #573216 7.5%, rgba(116, 77, 48, 0.9) 10%), repeating-radial-gradient(circle at -1000% 0%, #573216, #573216 0.1%, #744d30 0.4%, #744d30 0.5%)`
};

const table2: React.CSSProperties = {
  gridColumn: "6 / span 2", // 6列目〜7列目
  height: "100%",
  backgroundColor: "rgba(49, 23, 1, 0.623)",
  background: `repeating-radial-gradient(circle at -1000% 0%, rgba(116, 77, 48, 0.7), #573216 7.5%, rgba(116, 77, 48, 0.9) 10%), repeating-radial-gradient(circle at -1000% 0%, #573216, #573216 0.1%, #744d30 0.4%, #744d30 0.5%)`
};

const table2Style: React.CSSProperties = {
  gridColumn: "6 / span 2", // 6〜7列目
  gridRow: "1 / 2",        // ← 同じく全行に跨らせる
  width: "100%",
  height: "100%",
  backgroundColor: "#5c3a1e", // 深い茶色
  borderRadius: "12px",
  boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2), 0 4px 6px rgba(0,0,0,0.3)",
  border: "2px solid #3e2a14",
  backgroundImage: `linear-gradient(145deg, #6e4b2a, #4b2e1a)`,
};

const buttonContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10px',
};

const entranceStyle: React.CSSProperties = {
  // 他のスタイルプロパティ...
  width: '70px',
  height: '70px',
  backgroundImage: `url(/door.png)`, // 画像のパス
  backgroundSize: 'cover', // 画像サイズの調整方法
  // 他のスタイルプロパティ...
};

const StyledLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.8); /* 背景を薄く白くする */
`;


// const circle: React.CSSProperties = {
//   width: "60%",
//   height: "100%",
//   left: "20%",
//   position: "absolute",
//   backgroundColor: "rgba(49, 23, 1, 0.623)",
//   borderRadius: "50%",
//   background: `repeating-radial-gradient(circle at -1000% 0%, rgba(116, 77, 48, 0.7), #573216 7.5%, rgba(116, 77, 48, 0.9) 10%), repeating-radial-gradient(circle at -1000% 0%, #573216, #573216 0.1%, #744d30 0.4%, #744d30 0.5%)`
// };


// const image: React.CSSProperties = {
//   width: '400px',
//   aspectRatio: '1',
//   borderRadius: '50%',
//   border: 'dashed 1px #777',
//   margin: '50px',
//   position: 'relative',
// };


// const imageItem: React.CSSProperties = {
//   width: '80px',
//   height: '80px',
//   borderRadius: '50%',
//   background: '#777',
//   fontSize: '32px',
//   fontWeight: '700',
//   color: '#fff',
//   lineHeight: '80px',
//   textAlign: 'center',
//   position: 'absolute',
//   WebkitTransform: 'translate(-50%, -50%)',
//   transform: 'translate(-50%, -50%)',
// };


